import { Injectable } from '@angular/core';
import {
  Platform,
  LoadingController,
  LoadingOptions,
  AlertController,
  AlertButton,
  AlertOptions,
  ToastController,
} from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { checkEmailTypo } from './email-typo-checker';
import Bowser from 'bowser';
import { UrlSegment } from '@angular/router';
import { WindowService } from '../providers/window.service';
import { DateTime as LuxonDateTime } from 'luxon';
import { Period } from 'src/models/schedule-weekly-view.model';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Browser } from '@capacitor/browser';
import { Events } from 'src/providers/events/events';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

declare const window: any;

@Injectable()
export class Util {
  constructor(
    private platform: Platform,
    private windowService: WindowService,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private alertController: AlertController,
    private events: Events,
    private storage: TidyStorage
  ) {}

  getOs() {
    let os = 'other';
    const parser = Bowser.getParser(window.navigator.userAgent);
    const osName = parser.getOSName()?.toLowerCase();
    if (osName?.includes('mac')) {
      os = 'mac';
    } else if (osName?.includes('windows')) {
      os = 'windows';
    } else if (osName === 'android') {
      os = 'android';
    } else if (osName === 'ios') {
      os = 'ios';
    }
    return os;
  }

  getAppType() {
    if (this.platform.is('cordova')) {
      return 'native';
    } else if (this.isDesktop()) {
      return 'desktop web';
    } else if (!this.isDesktop()) {
      return 'mobile web';
    }
  }

  isCordova() {
    return this.platform.is('cordova');
  }

  isDesktop() {
    return this.windowService.isDesktopRes;
  }

  openUrl(url): void {
    // The in-app browser only opens http(s); mailto:, tel: and similar go to the system. Scheme-less links stay in the
    // in-app browser, since window.location.href would navigate the webview itself.
    const isSystemLink = /^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:/i.test(url);
    if (this.platform.is('cordova') && isSystemLink) {
      window.location.href = url;
    } else if (this.platform.is('cordova')) {
      Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
  }

  deepClone(object) {
    return JSON.parse(JSON.stringify(object));
  }

  checkEmailMistyping(emailToCheck) {
    return checkEmailTypo(emailToCheck);
  }

  removeCurrencyMask(value) {
    return value ? value.replace(/[$.,]/g, '') : null;
  }

  getEnvironment() {
    return environment;
  }

  copyToClipBoard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.showSuccess(new TranslationPipe().transform('Copied to clipboard'));
  }

  camelToSnakeCase(str): string {
    return str.replace(
      /[A-Z]|[\d.]+(?!$)(?!\d)/g,
      (letter) => `_${letter.toLowerCase()}`
    );
  }

  camelKeysToSnakeKeys(obj: object): object {
    const newObject = {};
    for (const key in obj) {
      if (typeof obj[key] === 'object') {
        newObject[this.camelToSnakeCase(key)] = this.camelKeysToSnakeKeys(
          obj[key]
        );
      } else {
        newObject[this.camelToSnakeCase(key)] = obj[key];
      }
    }
    return newObject;
  }

  encodeUrlSegment(value: Array<UrlSegment>): string {
    const strUrl = value.toString();
    const encodeRegex = new RegExp("'", 'g');
    const formatRegex = new RegExp(',', 'g');
    return strUrl.replace(encodeRegex, '%27').replace(formatRegex, '/');
  }

  isDateInsidePeriod(date: string, period: Period): boolean {
    const dateToCheck = LuxonDateTime.fromISO(date);
    const startDate = LuxonDateTime.fromISO(period.startDate);
    const endDate = LuxonDateTime.fromISO(period.endDate);
    return startDate <= dateToCheck && dateToCheck <= endDate;
  }

  getWeekdayFromDay(day: string): string {
    return LuxonDateTime.fromISO(day).toFormat('EEEE');
  }
  
  async showError(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-error toast-desktop' : 'toast-error',
      icon: 'alert-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showInfo(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-info toast-desktop' : 'toast-info',
      icon: 'information-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showSuccess(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-success toast-desktop' : 'toast-success',
      icon: 'checkmark-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async showWarning(message: string, duration = 5000): Promise<void> {
    const isDesktop = this.isDesktop();
    const toast = await this.toastCtrl.create({
      message: new TranslationPipe().transform(message),
      duration,
      cssClass: isDesktop ? 'toast-warning toast-desktop' : 'toast-warning',
      icon: 'alert-circle-outline',
      position: isDesktop ? 'top' : 'bottom',
    });
    toast.present();
  }

  async closeLastOpenedToast(): Promise<void> {
    const toast = await this.toastCtrl.getTop();
    if (toast) {
      toast.dismiss();
    }
  }

  async showLoading(params?: LoadingOptions): Promise<HTMLIonLoadingElement> {
    const options: LoadingOptions = {
      message: new TranslationPipe().transform('Loading...'),
      spinner: 'circles',
      mode: 'ios',
      ...params,
    };
    const loading = await this.loadingCtrl.create(options);
    loading.present();
    return loading;
  }

  async showAlert(
    header: string,
    message: string,
    backdropDismiss = false,
    buttons?: Array<AlertButton>
  ): Promise<HTMLIonAlertElement> {
    const alert = await this.alertController.create({
      header,
      message: new TranslationPipe().transform(message),
      buttons,
      backdropDismiss,
      mode: 'ios',
    });

    await alert.present();
    return alert;
  }

  async showConfirmAlert(options: AlertOptions): Promise<HTMLIonAlertElement> {
    const alert = await this.alertController.create({
      header: new TranslationPipe().transform('Confirm!'),
      message: new TranslationPipe().transform('Are you sure you want to do this?'),
      mode: 'ios',
      buttons: [
        {
          text: new TranslationPipe().transform('Go Back'),
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: new TranslationPipe().transform('Confirm'),
          role: 'confirm',
        },
      ],
      ...options,
    });
    alert.present();
    return alert;
  }

  parsePaymentMethods(paymentMethods) {
    return paymentMethods?.map((paymentMethod) => {
      let statusClass = '';
      let statusIcon = '';
      if (paymentMethod.status === 'succeeded') {
        statusClass = 'charge-badge-success';
        statusIcon = 'checkmark-circle';
      } else if (paymentMethod.status === 'action_required') {
        statusClass = 'charge-badge-pending';
        statusIcon = 'warning';
      }
      return {
        ...paymentMethod,
        class: statusClass,
        icon: statusIcon,
        status: paymentMethod.status,
      };
    });
  }

  private readonly letterGradients: { [key: string]: string } = {
    'A': 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    'B': 'linear-gradient(135deg, #b92b27 0%, #1565c0 100%)',
    'C': 'linear-gradient(135deg, #1f4037 0%, #99f2c8 100%)',
    'D': 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
    'E': 'linear-gradient(135deg, #009fff 0%, #ec2f4b 100%)',
    'F': 'linear-gradient(135deg, #da4453 0%, #89216b 100%)',
    'G': 'linear-gradient(135deg, #d66d75 0%, #e29587 100%)',
    'H': 'linear-gradient(135deg, #4568dc 0%, #b06ab3 100%)',
    'I': 'linear-gradient(135deg, #000046 0%, #1cb5e0 100%)',
    'J': 'linear-gradient(135deg, #283c86 0%, #45a247 100%)',
    'K': 'linear-gradient(135deg, #4e54c8 0%, #8f94fb 100%)',
    'L': 'linear-gradient(135deg, #0575e6 0%, #021b79 100%)',
    'M': 'linear-gradient(135deg, #3494e6 0%, #ec6ead 100%)',
    'N': 'linear-gradient(135deg, #2196f3 0%, #f44336 100%)',
    'O': 'linear-gradient(135deg, #4da0b0 0%, #d39d38 100%)',
    'P': 'linear-gradient(135deg, #8e0e00 0%, #1f1c18 100%)',
    'Q': 'linear-gradient(135deg, #360033 0%, #0b8793 100%)',
    'R': 'linear-gradient(135deg, #556270 0%, #ff6b6b 100%)',
    'S': 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)',
    'T': 'linear-gradient(135deg, #c04848 0%, #480048 100%)',
    'U': 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
    'V': 'linear-gradient(135deg, #ff512f 0%, #f09819 100%)',
    'W': 'linear-gradient(135deg, #348f50 0%, #56b4d3 100%)',
    'X': 'linear-gradient(135deg, #314755 0%, #26a0da 100%)',
    'Y': 'linear-gradient(135deg, #536976 0%, #292e49 100%)',
    'Z': 'linear-gradient(135deg, #1488cc 0%, #2b32b2 100%)'
  };

  getAvatarGradient(initials: string): string {
    const firstInitial = initials.charAt(0).toUpperCase() || 'A';
    return this.letterGradients[firstInitial] || this.letterGradients['A'];
  }

  async logUserOutUponUnexpectedError() {
    localStorage.setItem('wasLoggedOutDueToCacheIssue', 'true');
    this.events.publish('logout');
    localStorage.clear();
  }

  formatSourceDataValue(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return String(value);
  }
  
}
